import { useEffect, useState, useCallback } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { fmtMoney } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Plus, Trash2, CreditCard, CheckCircle2, Loader2, Send } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["draft", "sent", "paid"];

export default function InvoiceDetail() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [data, setData] = useState(null);
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("draft");
  const [saving, setSaving] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get(`/invoices/${id}`);
    setData(data);
    setItems(data.invoice.items || []);
    setStatus(data.invoice.status);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    let attempts = 0;
    setCheckingPayment(true);
    const tick = async () => {
      if (cancelled || attempts >= 10) { setCheckingPayment(false); return; }
      attempts++;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          toast.success("Payment received!");
          setCheckingPayment(false);
          setSearchParams({});
          await load();
          return;
        }
        setTimeout(tick, 2000);
      } catch { setTimeout(tick, 2000); }
    };
    tick();
    return () => { cancelled = true; };
  }, [sessionId, load, setSearchParams]);

  const updateItem = (idx, k, v) => {
    setItems((arr) => arr.map((it, i) => i === idx ? { ...it, [k]: k === "description" ? v : Number(v) } : it));
  };
  const addItem = () => setItems((arr) => [...arr, { description: "", quantity: 1, unit_price: 0 }]);
  const removeItem = (idx) => setItems((arr) => arr.filter((_, i) => i !== idx));
  const total = items.reduce((sum, it) => sum + (Number(it.quantity) || 0) * (Number(it.unit_price) || 0), 0);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/invoices/${id}`, { status, items });
      toast.success("Invoice saved");
      await load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const payNow = async () => {
    if (total <= 0) { toast.error("Invoice total must be greater than 0. Save first."); return; }
    try {
      const { data: r } = await api.post(`/invoices/${id}/checkout`, { origin_url: window.location.origin });
      window.location.href = r.url;
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Could not start payment");
    }
  };

  const emailInvoice = async () => {
    try {
      const { data: r } = await api.post(`/invoices/${id}/send`, { origin_url: window.location.origin });
      if (r.sent) toast.success(`Invoice emailed to ${r.to}`);
      else toast.message(r.message || "Email not sent", { description: `Share this link: ${r.pay_url}` });
      await load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    }
  };

  const sendReminder = async () => {
    try {
      const { data: r } = await api.post(`/invoices/${id}/send-reminder`, { origin_url: window.location.origin });
      if (r.sent) toast.success(`Reminder emailed to ${r.to} (stage ${r.stage})`);
      else toast.error(r.message || "Reminder not sent");
      await load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Could not send reminder");
    }
  };

  if (!data) {
    return (
      <div className="space-y-4">
        <div className="h-32 bg-white rounded-2xl border border-slate-200 animate-pulse" />
        <div className="h-64 bg-white rounded-2xl border border-slate-200 animate-pulse" />
      </div>
    );
  }
  const { invoice, customer, job } = data;

  return (
    <div className="space-y-5 max-w-3xl">
      <Link to="/invoices" className="inline-flex items-center text-sm text-slate-600 hover:text-slate-900 font-semibold">
        <ArrowLeft className="w-4 h-4 mr-1" /> Back to invoices
      </Link>

      {checkingPayment && (
        <div className="rounded-xl bg-blue-50 border border-blue-200 px-4 py-3 flex items-center gap-2 text-sm text-blue-900" data-testid="payment-checking">
          <Loader2 className="w-4 h-4 animate-spin" /> Confirming payment with Stripe…
        </div>
      )}

      <div className="bg-white rounded-2xl border border-slate-200 p-5 sm:p-6">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold">{invoice.number}</h1>
            <p className="text-slate-500 text-sm mt-1">
              {customer?.name} {job && <>• <Link to={`/jobs/${job.id}`} className="text-blue-600 hover:underline">{job.title}</Link></>}
            </p>
          </div>
          <StatusBadge status={invoice.status} />
        </div>

        <div className="mt-5">
          <Label className="text-sm font-semibold">Status</Label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="h-12 mt-1.5 w-48" data-testid="invoice-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              {STATUSES.map((s) => <SelectItem key={s} value={s}>{s[0].toUpperCase() + s.slice(1)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-5 sm:p-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-bold" style={{ fontFamily: "Outfit" }}>Line Items</h2>
          <Button onClick={addItem} variant="outline" className="h-10 px-3 rounded-xl" data-testid="add-line">
            <Plus className="w-4 h-4 mr-1" /> Add
          </Button>
        </div>
        <div className="space-y-2">
          {items.length === 0 && <p className="text-slate-500 text-sm">No line items yet.</p>}
          {items.map((it, idx) => (
            <div key={idx} className="grid grid-cols-12 gap-2 items-center" data-testid={`line-${idx}`}>
              <Input className="col-span-12 sm:col-span-6 h-11" placeholder="Description" value={it.description} onChange={(e) => updateItem(idx, "description", e.target.value)} />
              <Input className="col-span-4 sm:col-span-2 h-11" type="number" min="0" step="0.5" value={it.quantity} onChange={(e) => updateItem(idx, "quantity", e.target.value)} />
              <Input className="col-span-6 sm:col-span-3 h-11" type="number" min="0" step="0.01" value={it.unit_price} onChange={(e) => updateItem(idx, "unit_price", e.target.value)} />
              <button onClick={() => removeItem(idx)} className="col-span-2 sm:col-span-1 h-11 rounded-xl text-red-600 hover:bg-red-50 flex items-center justify-center" data-testid={`remove-line-${idx}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>

        <div className="mt-4 pt-4 border-t border-slate-200 flex items-center justify-between">
          <div className="text-slate-500 font-semibold">Total</div>
          <div className="text-2xl font-bold" style={{ fontFamily: "Outfit" }} data-testid="invoice-total">{fmtMoney(total)}</div>
        </div>

        <div className="mt-4 grid sm:grid-cols-3 gap-2">
          <Button onClick={save} disabled={saving} className="h-12 bg-blue-600 hover:bg-blue-700 font-semibold rounded-xl" data-testid="invoice-save">
            {saving ? "Saving…" : "Save Invoice"}
          </Button>
          <Button onClick={emailInvoice} variant="outline" className="h-12 font-semibold rounded-xl" data-testid="email-invoice">
            <Send className="w-4 h-4 mr-1.5" /> Email to Customer
          </Button>
          {invoice.status !== "paid" && invoice.sent_at && (
            <Button onClick={sendReminder} variant="outline" className="h-12 font-semibold rounded-xl border-amber-300 text-amber-800 hover:bg-amber-50" data-testid="send-reminder">
              <Send className="w-4 h-4 mr-1.5" /> Send Reminder
            </Button>
          )}
          {invoice.status !== "paid" && (
            <Button onClick={payNow} className="h-12 bg-green-600 hover:bg-green-700 font-semibold rounded-xl" data-testid="pay-now">
              <CreditCard className="w-4 h-4 mr-1.5" /> Pay Now
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
